import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export type FilterOperator =
  | "EQ"
  | "NEQ"
  | "CONTAINS"
  | "NOT_CONTAINS"
  | "HAS_PROPERTY"
  | "NOT_HAS_PROPERTY"
  | "GT"
  | "LT"
  | "GTE"
  | "LTE";

export interface CustomFieldFilter {
  fieldKey: string;
  operator: FilterOperator;
  value?: string;
}

interface PortalFilters {
  search: string;
  statusId: string | undefined;
  priority: string | undefined;
  assigneeId: string | undefined;
  assignedGroupId: string | undefined;
  workType: string | undefined;
  requestTypeIds: string[] | undefined;
  customFieldFilters: CustomFieldFilter[];
}

export interface UsePortalFiltersReturn extends PortalFilters {
  setSearch: (value: string) => void;
  setStatusId: (value: string | undefined) => void;
  setPriority: (value: string | undefined) => void;
  setAssigneeId: (value: string | undefined) => void;
  setAssignedGroupId: (value: string | undefined) => void;
  setWorkType: (value: string | undefined, resolvedRequestTypeIds?: string[]) => void;
  setRequestTypeIds: (ids: string[] | undefined) => void;
  setCustomFieldFilters: (filters: CustomFieldFilter[]) => void;
  hasActiveFilters: boolean;
  clearFilters: () => void;
}

export function usePortalFilters(): UsePortalFiltersReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  const search = searchParams.get("search") || "";
  const statusId = searchParams.get("statusId") || undefined;
  const priority = searchParams.get("priority") || undefined;
  const assigneeId = searchParams.get("assigneeId") || undefined;
  const assignedGroupId = searchParams.get("assignedGroupId") || undefined;
  const workType = searchParams.get("workType") || undefined;
  const requestTypeIds = useMemo(() => {
    const ids = searchParams.get("requestTypeIds");
    return ids ? ids.split(",") : undefined;
  }, [searchParams]);

  const customFieldFilters = useMemo<CustomFieldFilter[]>(() => {
    const raw = searchParams.get("cfFilters");
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.filter(
          (f: unknown) =>
            typeof f === "object" &&
            f !== null &&
            "fieldKey" in f &&
            "operator" in f &&
            typeof (f as CustomFieldFilter).fieldKey === "string" &&
            typeof (f as CustomFieldFilter).operator === "string",
        ) as CustomFieldFilter[];
      }
    } catch {
      // Invalid JSON — ignore
    }
    return [];
  }, [searchParams]);

  const updateParam = useCallback(
    (key: string, value: string | undefined) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) {
            next.set(key, value);
          } else {
            next.delete(key);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setSearch = useCallback(
    (value: string) => updateParam("search", value || undefined),
    [updateParam],
  );

  const setStatusId = useCallback(
    (value: string | undefined) => updateParam("statusId", value),
    [updateParam],
  );

  const setPriority = useCallback(
    (value: string | undefined) => updateParam("priority", value),
    [updateParam],
  );

  const setAssigneeId = useCallback(
    (value: string | undefined) => updateParam("assigneeId", value),
    [updateParam],
  );

  const setAssignedGroupId = useCallback(
    (value: string | undefined) => updateParam("assignedGroupId", value),
    [updateParam],
  );

  const setWorkType = useCallback(
    (value: string | undefined, resolvedRequestTypeIds?: string[]) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) {
            next.set("workType", value);
          } else {
            next.delete("workType");
          }
          if (resolvedRequestTypeIds?.length) {
            next.set("requestTypeIds", resolvedRequestTypeIds.join(","));
          } else {
            next.delete("requestTypeIds");
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setRequestTypeIds = useCallback(
    (ids: string[] | undefined) => {
      updateParam("requestTypeIds", ids?.length ? ids.join(",") : undefined);
    },
    [updateParam],
  );

  const setCustomFieldFilters = useCallback(
    (filters: CustomFieldFilter[]) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (filters.length > 0) {
            next.set("cfFilters", JSON.stringify(filters));
          } else {
            next.delete("cfFilters");
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const hasActiveFilters = !!(
    search ||
    statusId ||
    priority ||
    assigneeId ||
    assignedGroupId ||
    customFieldFilters.length > 0
  );

  const clearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  return {
    search,
    statusId,
    priority,
    assigneeId,
    assignedGroupId,
    workType,
    requestTypeIds,
    customFieldFilters,
    setSearch,
    setStatusId,
    setPriority,
    setAssigneeId,
    setAssignedGroupId,
    setWorkType,
    setRequestTypeIds,
    setCustomFieldFilters,
    hasActiveFilters,
    clearFilters,
  };
}
